import { useNavigate } from 'react-router-dom';
import { getDashboard, mediaUrl, type DashboardData } from '../api';
import { useApi } from '../useApi';
import { badgeClass, fmtDate } from '../format';

/** Relative time: <60s "just now", <60m "N min ago", <24h "N h ago", else "N d ago". */
function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

const ACTIVITY_DOT: Record<DashboardData['liveActivity'][number]['kind'], string> = {
  payment: 'var(--accent)',
  match: 'var(--pink)',
  report: 'var(--danger)',
};

/** Map a series to [x, y] pixel coordinates over n equally-spaced x slots. */
function coords(values: number[], max: number, width: number, height: number, padTop: number, padBottom: number): [number, number][] {
  const n = values.length;
  if (n === 0) return [];
  const span = height - padTop - padBottom;
  return values.map((v, i) => {
    const x = n === 1 ? width / 2 : (i / (n - 1)) * width;
    const y = padTop + (span - (v / max) * span);
    return [x, y];
  });
}

const toPoints = (pts: [number, number][]): string =>
  pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

export function Dashboard() {
  const navigate = useNavigate();
  const { data, error } = useApi(getDashboard);

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (error) {
    return (
      <div className="admin-wrap">
        <p className="error-line">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="admin-wrap">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const c = data.cards;

  // --- signups & matches chart ---
  const series = data.signupsMatches30d;
  const sumSignups = series.reduce((a, p) => a + p.signups, 0);
  const sumMatches = series.reduce((a, p) => a + p.matches, 0);
  const chartW = 640;
  const chartH = 200;
  const padTop = 14;
  const padBottom = 6;
  const yMax = Math.max(1, ...series.map((p) => Math.max(p.signups, p.matches)));
  const signupPts = coords(series.map((p) => p.signups), yMax, chartW, chartH, padTop, padBottom);
  const matchPts = coords(series.map((p) => p.matches), yMax, chartW, chartH, padTop, padBottom);
  const signupPoints = toPoints(signupPts);
  const matchPoints = toPoints(matchPts);
  const areaPath =
    signupPts.length > 1
      ? `M${signupPts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L')} L${chartW},${chartH} L0,${chartH} Z`
      : '';

  // --- revenue by week ---
  const weeks = data.revenueByWeek;
  const maxTon = Math.max(0, ...weeks.map((w) => w.ton));
  const peakTon = maxTon;

  // --- revenue split ---
  const split = data.revenueSplit30d;
  const splitRows: { label: string; ton: number; color: string }[] = [
    { label: 'Subscriptions', ton: split.subscription, color: 'var(--accent)' },
    { label: 'Gifts', ton: split.gift, color: 'var(--pink)' },
    { label: 'Boosts & Sparks', ton: split.boost, color: 'var(--blue)' },
    { label: 'Event deposits', ton: split.event_deposit, color: 'var(--ok)' },
  ];
  const maxSplit = Math.max(0, ...splitRows.map((r) => r.ton));

  return (
    <div className="admin-wrap">
      <div className="dash-head">
        <div>
          <h1>Dashboard</h1>
          <p className="sub">{today} · all systems nominal</p>
        </div>
        {data.botOnline ? (
          <span className="pill ok">
            <span className="led" />
            BOT ONLINE
          </span>
        ) : (
          <span className="pill">
            <span className="led" />
            BOT OFFLINE
          </span>
        )}
      </div>

      <div className="kpi-grid">
        <div className="kpi">
          <span className="kpi-label">Total Users</span>
          <span className="kpi-value">{c.totalUsers.toLocaleString()}</span>
          <span className="kpi-delta up">+{c.usersToday.toLocaleString()} today</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Active Today</span>
          <span className="kpi-value">{c.activeToday.toLocaleString()}</span>
          <span className="kpi-delta">today</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Matches Today</span>
          <span className="kpi-value">{c.matchesToday.toLocaleString()}</span>
          <span className="kpi-delta">today</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Revenue · 30d</span>
          <span className="kpi-value">{Math.round(c.revenue30dTon).toLocaleString()} TON</span>
          <span className="kpi-delta">30 days</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Premium Subs</span>
          <span className="kpi-value">{c.premiumSubs.toLocaleString()}</span>
          <span className="kpi-delta up">+{c.premiumSubsThisWeek.toLocaleString()} this week</span>
        </div>
        <div className="kpi urgent">
          <span className="kpi-label">Open Reports</span>
          <span className="kpi-value">{c.openReports.toLocaleString()}</span>
          <span className="kpi-delta down">{c.urgentReports.toLocaleString()} urgent</span>
        </div>
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card-head">
            <span className="card-title">Signups &amp; Matches · 30 days</span>
            <div className="legend">
              <span>
                <i style={{ background: 'var(--accent)' }} />
                Signups
              </span>
              <span>
                <i style={{ background: 'var(--pink)' }} />
                Matches
              </span>
            </div>
          </div>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>
            {sumSignups.toLocaleString()} signups · {sumMatches.toLocaleString()} matches
          </div>
          <svg
            className="chart-svg"
            viewBox={`0 0 ${chartW} ${chartH}`}
            preserveAspectRatio="none"
            role="img"
            aria-label="Signups and matches over the last 30 days"
          >
            {areaPath && <path d={areaPath} fill="var(--accent)" fillOpacity={0.12} />}
            {signupPoints && (
              <polyline points={signupPoints} fill="none" stroke="var(--accent)" strokeWidth={2} />
            )}
            {matchPoints && (
              <polyline points={matchPoints} fill="none" stroke="var(--pink)" strokeWidth={2} />
            )}
          </svg>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">Live Activity</span>
          </div>
          {data.liveActivity.length === 0 ? (
            <p className="muted">No recent activity.</p>
          ) : (
            <div className="activity-list">
              {data.liveActivity.map((a, i) => (
                <div className="activity-row" key={i}>
                  <span className="a-dot" style={{ background: ACTIVITY_DOT[a.kind] }} />
                  <div>
                    <div className="a-text">{a.text}</div>
                    <div className="a-time">{relTime(a.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card-head">
            <span className="card-title">Revenue · TON / week</span>
          </div>
          {weeks.length === 0 ? (
            <p className="muted">No paid TON revenue yet.</p>
          ) : (
            <div className="bars">
              {weeks.map((w, i) => {
                const isPeak = peakTon > 0 && w.ton === peakTon;
                const pct = maxTon > 0 ? (w.ton / maxTon) * 100 : 0;
                return (
                  <div className={`bar-col${isPeak ? ' peak' : ''}`} key={`${w.label}-${i}`}>
                    <span className="bar-val">{w.ton.toLocaleString()}</span>
                    <span className="bar" style={{ height: `${pct}%` }} />
                    <span className="bar-label">{w.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">Revenue Split · 30d</span>
          </div>
          {splitRows.map((r) => {
            const pct = maxSplit > 0 ? (r.ton / maxSplit) * 100 : 0;
            return (
              <div className="split-row" key={r.label}>
                <div className="split-head">
                  <b>{r.label}</b>
                  <span className="amt">{r.ton.toLocaleString()} TON</span>
                </div>
                <div className="split-track">
                  <div className="split-fill" style={{ width: `${pct}%`, background: r.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <span className="card-title">Newest Members</span>
        </div>
        <div className="table-wrap">
          <table className="flm">
            <thead>
              <tr>
                <th>User</th>
                <th>City</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Last active</th>
              </tr>
            </thead>
            <tbody>
              {data.newestMembers.map((m) => (
                <tr key={m.id} className="clickable" onClick={() => navigate('/users/' + m.id)}>
                  <td>
                    <div className="user-cell">
                      {m.photoUrl ? (
                        <img
                          className="avatar sm"
                          src={mediaUrl(m.photoUrl)}
                          alt={m.name}
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <span className="avatar sm">{(m.name || '?').charAt(0).toUpperCase()}</span>
                      )}
                      <div>
                        <div className="uc-name">{m.name}</div>
                        <div className="uc-sub">@{m.username ?? '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td>{m.city ?? '—'}</td>
                  <td>
                    <span className={`plan-badge ${m.plan}`}>{m.plan}</span>
                  </td>
                  <td>
                    <span className={badgeClass(m.status)}>{m.status}</span>
                  </td>
                  <td>{fmtDate(m.joinedAt)}</td>
                  <td>{m.lastActiveAt ? relTime(m.lastActiveAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
